import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { PrepaidSettlement } from "../../components/payments/PrepaidSettlement";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  QrCode, Package, Scales, Play, CheckCircle, MapPin, Phone, User,
  Keyboard, Plus, Warning, WhatsappLogo, ArrowRight, X, Camera,
  Receipt, Lightning, Clock, ListChecks, ArrowLeft, Trash, Info,
  Truck, Minus,
} from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";
import { staffApi, getGps, formatStaffErr } from "../../lib/staffApi";
import { extraChargeLabel } from "../../lib/labels";

const PRIORITY_STYLE = {
  NORMAL: "bg-[#D4E0D1] text-[#1A3317]",
  HIGH: "bg-[#F9E4B7] text-[#7A5A11]",
  URGENT: "bg-[#F4C7B9] text-[#7A2E18]",
  EMERGENCY: "bg-[#F4C7B9] text-[#7A2E18]",
};

/**
 * Field Staff · Task Detail (Iter 43 rewrite).
 * Guided workflow per new spec:
 *   1. Auto-fetch full context (customer + package + rules + QR bags).
 *   2. If package uses QR bags → Scan (recommended) or Manual entry.
 *      · Continuous camera scan; on decode → validate + fetch bag colour/
 *        type/waste category → weight entry restricted to package-included
 *        waste types → Save → "Add another bag" → reopens scanner.
 *   3. If package does NOT use QR bags → direct multi-category weight entry.
 *   4. Live collection summary; final Confirm → GPS + Chain of Custody +
 *      draft/final invoice per tenant setting.
 */
export default function StaffTaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null); // /collection-context response
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Working state
  const [scanOpen, setScanOpen] = useState(false); // camera live scan
  const [entryFor, setEntryFor] = useState(null);  // {qr_code, color, bag_type, status, waste_category, waste_type_id}
  const [entryWeights, setEntryWeights] = useState({}); // waste_type_id → kg (for entryFor)
  const [manualMode, setManualMode] = useState(false); // typing QR instead of scanning
  const [manualCode, setManualCode] = useState("");
  const [bags, setBags] = useState([]); // scanned+weighed bags queued
  const [nonQrWeights, setNonQrWeights] = useState({}); // waste_type_id → kg (non-QR mode)
  const [extras, setExtras] = useState([]);
  const [notes, setNotes] = useState("");
  // Iter 170 — customer's in-house bags for the multi-select Start-Job flow.
  const [assignedBags, setAssignedBags] = useState({ in_house: [], totals: null });
  const [selectedAssigned, setSelectedAssigned] = useState(new Set());
  const [bulkKgPerBag, setBulkKgPerBag] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await staffApi.get(`/staff-app/tasks/${id}/collection-context`);
      setCtx(r.data);
      // Hydrate any previously collected bags so re-opening the task
      // doesn't lose progress.
      const defaultWt = (r.data.included_types || [])[0]?.waste_type_id;
      const prev = (r.data.collected_bags || []).map((b) => {
        const wt = b.waste_type_id || defaultWt;
        const kg = Number(b.collected_weight_kg || b.weight_kg || 0);
        return {
          qr_code: b.qr_code,
          color: b.color,
          bag_type: b.bag_type,
          waste_type_id: wt,
          waste_category: b.waste_category,
          waste_name: b.waste_name
            || (r.data.included_types || []).find((t) => t.waste_type_id === wt)?.waste_name,
          weight_kg: kg,
          weights: wt ? { [wt]: kg } : {},
          manual: !!b.manual_flag,
          already_saved: true, // read-only; server-side
        };
      });
      setBags(prev);
      // Iter 170 — fetch the customer's currently in-house bags so the
      // field staff can multi-select and mark them collected in one tap.
      try {
        const cb = await staffApi.get(`/staff-app/tasks/${id}/customer-bags`);
        setAssignedBags(cb.data || { in_house: [], totals: null });
      } catch { /* non-critical */ }
    } catch (e) {
      setErr(formatStaffErr(e) || "Failed to load task");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const startTask = async () => {
    setBusy(true);
    try {
      await staffApi.post(`/staff-app/tasks/${id}/start`);
      toast.success("Task started");
      load();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  // Iter 170 — multi-select "quick collect" for bags already at the customer.
  const toggleAssigned = (qr) => {
    setSelectedAssigned(prev => {
      const n = new Set(prev);
      if (n.has(qr)) n.delete(qr); else n.add(qr);
      return n;
    });
  };
  const selectAllAssigned = () => {
    const all = (assignedBags.in_house || []).map(b => b.qr_code);
    setSelectedAssigned(prev =>
      prev.size === all.length ? new Set() : new Set(all),
    );
  };
  const collectSelectedAssigned = async () => {
    if (selectedAssigned.size === 0) {
      toast.error("Select at least one bag"); return;
    }
    // Iter 189 — the input now captures TOTAL weight across selected
    // bags. Distribute evenly per bag.
    const totalKg = Number(bulkKgPerBag);
    if (!Number.isFinite(totalKg) || totalKg <= 0) {
      toast.error("Enter total weight (kg) for the selected bags first"); return;
    }
    const perBag = totalKg / selectedAssigned.size;
    setBusy(true);
    try {
      const gps = await getGps().catch(() => ({}));
      const items = [...selectedAssigned].map(qr => ({ qr_code: qr, weight_kg: perBag }));
      const r = await staffApi.post(`/staff-app/tasks/${id}/collect-bulk`, {
        items, latitude: gps.latitude, longitude: gps.longitude,
      });
      const d = r.data || {};
      toast.success(
        `${d.collected_count} bag${d.collected_count === 1 ? "" : "s"} collected · ${d.total_weight_kg} kg`
          + (d.skipped_count ? ` · ${d.skipped_count} skipped` : ""),
      );
      if (d.skipped_count && d.skipped?.length) {
        d.skipped.slice(0, 3).forEach(s => toast.info(`Skipped ${s.qr_code}: ${s.reason}`));
      }
      setSelectedAssigned(new Set());
      setBulkKgPerBag("");
      load();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  // ── Scan flow ──
  const openScanner = () => {
    setEntryFor(null); setEntryWeights({});
    setManualCode(""); setManualMode(false);
    setScanOpen(true);
  };
  const openManualEntry = () => {
    setEntryFor(null); setEntryWeights({});
    setManualCode(""); setManualMode(true);
    setScanOpen(true);
  };

  const validateAndFetch = useCallback(async (code) => {
    const cleaned = String(code || "").trim().toUpperCase();
    if (!cleaned) return;
    if (bags.some((b) => b.qr_code === cleaned)) {
      toast.error(`Bag ${cleaned} already added to this session`);
      return;
    }
    setBusy(true);
    try {
      const r = await staffApi.get(
        `/staff-app/qr/${encodeURIComponent(cleaned)}?task_id=${id}`,
      );
      const d = r.data;
      // Server already rejects collected/other-customer, but double-check.
      if (["collected", "received", "processed", "cancelled", "void", "disposed"]
          .includes((d.status || "").toLowerCase())) {
        toast.error(`Bag ${cleaned} is ${d.status}`);
        setBusy(false); return;
      }
      // Pre-fill weight for the bag's waste type only (if bag has one)
      const initWeights = {};
      if (d.waste_type_id) initWeights[d.waste_type_id] = "";
      setEntryFor(d);
      setEntryWeights(initWeights);
    } catch (e) {
      const msg = e?.response?.data?.detail || formatStaffErr(e);
      toast.error(String(msg));
    }
    setBusy(false);
  }, [bags, id]);

  const saveBagEntry = () => {
    if (!entryFor) return;
    // Validate at least one weight entered
    const parsed = Object.entries(entryWeights)
      .map(([k, v]) => [k, Number(v || 0)])
      .filter(([, v]) => v > 0);
    if (parsed.length === 0) {
      toast.error("Enter weight for at least one waste type");
      return;
    }
    // For QR bags we allow one waste_type per bag (spec). Take first > 0.
    const [wt, kg] = parsed[0];
    setBags((bs) => [
      ...bs,
      {
        qr_code: entryFor.qr_code,
        color: entryFor.color,
        bag_type: entryFor.bag_type,
        waste_type_id: wt,
        waste_category: entryFor.waste_category,
        waste_name: (ctx?.included_types || []).find((t) => t.waste_type_id === wt)?.waste_name,
        weight_kg: kg,
        manual: manualMode,
      },
    ]);
    toast.success(`Bag ${entryFor.qr_code} · ${kg} kg saved`);
    setEntryFor(null); setEntryWeights({});
    setManualCode(""); setScanOpen(false);
  };

  const removeBag = (code) => setBags((bs) => bs.filter((b) => b.qr_code !== code));

  const total = useMemo(() => {
    // In QR mode, sum both scanned bags AND any non-QR weights entered
    // for the allow-listed categories (superadmin override).
    const rows = [
      ...(ctx?.use_qr_bags ? bags : []).map((b) => ({ waste_type_id: b.waste_type_id, kg: Number(b.weight_kg) })),
      ...Object.entries(nonQrWeights).map(([wt, kg]) => ({ waste_type_id: wt, kg: Number(kg || 0) })),
    ];
    // Group by CATEGORY (per-category allowance is the package rule).
    const wtCatMap = Object.fromEntries(
      (ctx?.included_types || []).map((it) => [it.waste_type_id, it.category]),
    );
    const perCategory = {};
    const perType = {};
    let totalKg = 0;
    for (const r of rows) {
      if (!r.kg || !r.waste_type_id) continue;
      perType[r.waste_type_id] = (perType[r.waste_type_id] || 0) + r.kg;
      const cat = wtCatMap[r.waste_type_id];
      if (cat) perCategory[cat] = (perCategory[cat] || 0) + r.kg;
      totalKg += r.kg;
    }
    const bagsN = ctx?.use_qr_bags
      ? bags.length + Object.values(nonQrWeights).filter((v) => Number(v) > 0).length
      : Object.values(nonQrWeights).filter((v) => Number(v) > 0).length;
    return { perType, perCategory, totalKg, bagsN };
  }, [bags, nonQrWeights, ctx?.use_qr_bags, ctx?.included_types]);

  // Iter 187 — confirm modal state; submit() opens it, doSubmit() performs
  // the POST after the user taps Confirm.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const submit = () => {
    if (!ctx?.package?.id) {
      toast.error("No package on this task"); return;
    }
    // Fail fast BEFORE opening the modal so the field staff sees the
    // toast on the entry they need to fix.
    const anyBags = ctx.use_qr_bags
      ? bags.some((b) => Number(b.weight_kg) > 0 && b.waste_type_id)
      : false;
    const anyNonQr = Object.values(nonQrWeights).some((v) => Number(v) > 0);
    if (!anyBags && !anyNonQr && (ctx.collected_bags || []).length === 0) {
      toast.error("Add at least one bag / weight before confirming"); return;
    }
    setConfirmOpen(true);
  };
  const doSubmit = async () => {
    const qrLines = ctx.use_qr_bags
      ? bags
          .filter((b) => Number(b.weight_kg) > 0 && b.waste_type_id)
          .map((b) => ({
            waste_type_id: b.waste_type_id,
            quantity_kg: Number(b.weight_kg),
            qr_code: b.qr_code,
            manual: b.manual,
          }))
      : [];
    const nonQrLines = Object.entries(nonQrWeights)
      .filter(([, kg]) => Number(kg) > 0)
      .map(([wt, kg]) => ({ waste_type_id: wt, quantity_kg: Number(kg), manual: false }));
    const wasteLines = [...qrLines, ...nonQrLines];
    setBusy(true);
    try {
      const gps = await getGps().catch(() => ({ latitude: 0, longitude: 0, accuracy: null, address: null }));
      const r = await staffApi.post(`/staff-app/tasks/${id}/draft-invoice`, {
        bag_type: ctx.use_qr_bags
          ? (Object.values(nonQrWeights).some((v) => Number(v) > 0) ? "mixed" : "qr")
          : "non_qr",
        primary: {
          package_id: ctx.package.id,
          waste_category: (ctx.included_types || [])[0]?.category,
          waste_lines: wasteLines,
        },
        cross_sells: [],
        extra_charge_ids: extras,
        notes: notes || null,
        distance_km: 0,
        latitude: gps.latitude, longitude: gps.longitude,
        gps_accuracy: gps.accuracy || null, gps_address: gps.address || null,
      });
      const isDraft = ctx.invoice_verification_required;
      toast.success(
        isDraft
          ? `Draft invoice ${r.data.invoice_no} created · pending office verification`
          : `Invoice ${r.data.invoice_no} issued`,
      );
      setConfirmOpen(false);
      nav("/staff-app/tasks");
    } catch (e) {
      toast.error(formatStaffErr(e));
    }
    setBusy(false);
  };

  // Iter 187 — pre-submit totals for the confirm modal.
  const previewTotals = useMemo(() => {
    // Extra charges: sum(amount) grouped by id×count.
    const extraCounts = extras.reduce((m, id) => { m[id] = (m[id] || 0) + 1; return m; }, {});
    const extraSum = Object.entries(extraCounts).reduce((sum, [eid, qty]) => {
      const x = (ctx?.extra_charges || []).find((e) => e.id === eid);
      return sum + (x ? Number(x.amount) * qty : 0);
    }, 0);
    // Package base price.
    const pkgPrice = Number(ctx?.package?.price || 0);
    // Excess = sum over categories of max(0, collectedKg - allowedKg) × excessRate.
    let excessSum = 0;
    (ctx?.category_allowances || []).forEach((ca) => {
      const kg = total.perCategory[ca.category] || 0;
      const over = ca.allowed_quantity_kg > 0 ? Math.max(0, kg - ca.allowed_quantity_kg) : 0;
      excessSum += over * Number(ca.excess_rate_per_kg || 0);
    });
    const payable = pkgPrice + excessSum + extraSum;
    return { pkgPrice, excessSum, extraSum, extraCounts, payable };
  }, [extras, ctx?.extra_charges, ctx?.package, ctx?.category_allowances, total]);

  if (err) {
    return (
      <div className="p-4 bg-[#F9EDE7] text-[#7A2E18] rounded" data-testid="task-err">
        {err}
        <button onClick={() => nav("/staff-app/tasks")} className="ml-2 underline">Back</button>
      </div>
    );
  }
  if (!ctx) return <div className="text-sm text-[#8A8780]">Loading…</div>;

  const t = ctx.task;
  const c = ctx.customer || {};
  const pkg = ctx.package;
  const sr = ctx.service_request || {};
  const status = t.status;
  const started = status === "in_progress";
  const finished = status === "completed" || status === "missed";
  const canWork = started && !finished;

  return (
    <div className="space-y-5 pb-24" data-testid="staff-task-detail">
      {/* Back */}
      <button onClick={() => nav("/staff-app/tasks")}
              className="text-xs text-[#5C5A55] inline-flex items-center gap-1 hover:text-[#1A1A1A]"
              data-testid="task-back">
        <ArrowLeft size={12} /> Back to tasks
      </button>

      {/* Task hero */}
      <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 space-y-2" data-testid="task-hero">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em]">
          {t.sr_priority && (
            <span className={`px-1.5 py-0.5 rounded ${PRIORITY_STYLE[t.sr_priority] || "bg-[#F7F6F2]"}`}>
              <Lightning size={9} className="inline mr-0.5" weight="fill" />{t.sr_priority}
            </span>
          )}
          {sr.request_no && <span className="text-[#8A8780] font-mono">{sr.request_no}</span>}
          <span className="text-[#8A8780]">· {t.date}</span>
          <span className="ml-auto text-[#2D5A27] uppercase inline-flex items-center gap-1">
            <CheckCircle size={10} weight="fill" />{status}
          </span>
        </div>

        {/* Customer info — Iter 187: house_no big, priority tier badge,
             landmark, vehicle access, primary waste chip. */}
        <div className="flex items-start gap-3 pt-1">
          <div className="w-11 h-11 rounded-md bg-[#D4E0D1] flex items-center justify-center">
            <User size={20} color="#2D5A27" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-heading text-lg leading-tight truncate">{c.name || "—"}</div>
              {c.priority_tier && c.priority_tier !== "regular" && (
                <span data-testid={`task-priority-tier-${c.priority_tier}`}
                       title="Priority tier — staff-only badge"
                       className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.16em] ${
                         c.priority_tier === "vvip"
                           ? "bg-[#6D28D9] text-white"
                           : "bg-[#A855F7] text-white"
                       }`}>
                  {c.priority_tier}
                </span>
              )}
            </div>
            {c.phone && (
              <a href={`tel:${c.phone}`} className="text-xs text-[#2D5A27] font-mono inline-flex items-center gap-1">
                <Phone size={12} weight="fill" />{c.phone}
              </a>
            )}
            {/* House number — big prominent line so field staff can spot the
                 door from the road */}
            {c.house_no && (
              <div className="mt-1 text-2xl font-heading text-[#1A1A1A] leading-none"
                    data-testid="task-house-no">
                #{c.house_no}
              </div>
            )}
            {c.landmark && (
              <div className="text-[12px] text-[#7A5A11] mt-1"
                    data-testid="task-landmark">
                <MapPin size={11} weight="fill" className="inline -mt-0.5 mr-0.5"/>
                Landmark: <strong>{c.landmark}</strong>
              </div>
            )}
            {c.address && (
              <div className="text-[11px] text-[#5C5A55] mt-0.5">
                {c.address}
              </div>
            )}
            {(c.lsgd_name || c.ward_name) && (
              <div className="text-[10px] text-[#8A8780] mt-0.5">
                {[c.lsgd_name, c.ward_name].filter(Boolean).join(" · ")}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {(c.primary_waste_names || []).length > 0 && (
                <span data-testid="task-primary-waste"
                       className="text-[10px] px-1.5 py-0.5 rounded bg-[#F0F7EE] text-[#1A3317] border border-[#B5D5AC] font-semibold">
                  Primary waste: {(c.primary_waste_names || []).join(", ")}
                </span>
              )}
              {c.vehicle_access !== null && c.vehicle_access !== undefined && (
                <span data-testid="task-vehicle-access"
                       className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border ${
                         c.vehicle_access
                           ? "bg-[#F0F7EE] text-[#1A3317] border-[#B5D5AC]"
                           : "bg-[#F9EDE7] text-[#7A2E18] border-[#F4C7B9]"
                       }`}>
                  <Truck size={9} weight="fill" className="inline -mt-0.5 mr-0.5"/>
                  Vehicle {c.vehicle_access ? "OK" : "NO"}
                  {c.vehicle_type ? ` · ${c.vehicle_type}` : ""}
                </span>
              )}
              {c.haritha_mithram_id && (
                <span className="text-[10px] text-[#7A5A11] font-mono">
                  HM: {c.haritha_mithram_id}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Nav / Call / WhatsApp */}
        <div className="flex gap-2 pt-1">
          {c.latitude && c.longitude && (
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${c.latitude},${c.longitude}`}
                target="_blank" rel="noreferrer" data-testid="task-navigate"
                onClick={() => {
                  // Iter 204 · fire-and-forget — stamp
                  // navigation_started_at on the task so the customer's
                  // detail page flips to live tracking (ETA + distance).
                  staffApi.post(`/staff-app/tasks/${id}/navigation-started`)
                    .catch(() => {});
                }}
                className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center justify-center gap-1.5 hover:bg-[#F7F6F2]">
              <MapPin size={13} weight="fill" />Navigate
            </a>
          )}
          {c.phone && (
            <a href={`tel:${c.phone}`} data-testid="task-call"
                className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-xs font-semibold inline-flex items-center justify-center gap-1.5">
              <Phone size={13} weight="fill" />Call
            </a>
          )}
          {c.phone && (
            <a href={`https://wa.me/${(c.phone || "").replace(/\D/g, "")}`}
                target="_blank" rel="noreferrer" data-testid="task-whatsapp"
                className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center justify-center gap-1.5 hover:bg-[#F7F6F2]">
              <WhatsappLogo size={13} weight="fill" />WhatsApp
            </a>
          )}
        </div>
      </div>

      {/* Iter 195 — Bag delivery section. ALWAYS visible so the staff
           can confirm at a glance whether they also have a delivery to
           make for this customer today. Positioned before the service
           package so it can't be missed. */}
      <div className="bg-white border border-[#E5E2D9] rounded-lg p-4"
            data-testid="task-bag-delivery-section">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
          Bag delivery
        </div>
        {ctx.pending_bag_delivery ? (
          <button
            type="button"
            onClick={() => nav(`/staff-app/deliveries?open=${ctx.pending_bag_delivery.id}`)}
            data-testid="task-pending-bag-delivery-banner"
            className="w-full text-left bg-[#F9E4B7] border-2 border-[#C25D38] rounded-md p-3 flex items-center gap-3 hover:bg-[#F4C7B9]">
            <Package size={22} weight="duotone" className="text-[#7A2E18] flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#7A2E18]">
                Bag delivery pending for this customer
              </div>
              <div className="text-[11px] text-[#5C5A55] mt-0.5">
                Challan <b className="font-mono">{ctx.pending_bag_delivery.challan_no}</b>
                {ctx.pending_bag_delivery.bag_count != null && (
                  <> · {ctx.pending_bag_delivery.bag_count} bag{ctx.pending_bag_delivery.bag_count !== 1 ? "s" : ""}</>
                )}
                {ctx.pending_bag_delivery.status && (
                  <> · {(ctx.pending_bag_delivery.status || "").replace(/_/g, " ")}</>
                )} · complete delivery first, then collect
              </div>
            </div>
            <ArrowRight size={14} weight="bold" className="text-[#7A2E18]"/>
          </button>
        ) : (
          <div data-testid="task-no-bag-delivery"
                className="flex items-center gap-2 text-[12px] text-[#5C5A55] bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-3">
            <Package size={16} weight="regular" className="text-[#8A8780] flex-shrink-0"/>
            <span>No bag delivery assigned to you for this customer today.</span>
          </div>
        )}
      </div>

      {/* Package + Included items info */}
      {pkg && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 space-y-2" data-testid="task-package-info">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Service package</div>
          <div className="font-heading text-base inline-flex items-center gap-1.5">
            <Package size={14} className="text-[#2D5A27]" />{pkg.name}
            <span className="text-[#8A8780] text-xs">· ₹{pkg.price}</span>
          </div>
          {sr.frequency && (
            <div className="text-[11px] text-[#5C5A55]">
              <Clock size={10} className="inline -mt-0.5"/> Frequency: <strong>{sr.frequency}</strong>
            </div>
          )}
          {sr.special_instructions && (
            <div className="text-[11px] bg-[#F9E4B7]/30 border border-[#F9E4B7] rounded p-2 text-[#7A5A11] mt-1">
              <Info size={10} className="inline -mt-0.5 mr-0.5"/> {sr.special_instructions}
            </div>
          )}
          {ctx.included_types.length > 0 && (
            <div className="pt-2 border-t border-[#F0EDE4]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-1.5">
                Included waste types
              </div>
              <div className="space-y-1">
                {ctx.included_types.map((it) => (
                  <div key={it.waste_type_id} className="flex items-center justify-between text-[11px] bg-[#F7F6F2] rounded px-2 py-1">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-[#1A1A1A]">{it.waste_name}</span>
                      <span className="text-[#8A8780] ml-1">· {it.category}</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Category-level allowance (the package's `items[]` limit is
                  a total across all included waste_types of the same
                  category, not per waste_type). */}
              {(ctx.category_allowances || []).map((ca) => (
                <div key={ca.category} className="mt-2 flex items-center justify-between text-[11px] bg-[#D4E0D1]/40 rounded px-2 py-1.5">
                  <span className="font-medium text-[#2D5A27]">
                    {ca.category} · total allowance
                  </span>
                  <span className="text-[10px] text-[#2D5A27] font-mono">
                    {ca.allowed_quantity_kg} kg incl · ₹{ca.excess_rate_per_kg}/excess kg
                  </span>
                </div>
              ))}
            </div>
          )}
          {ctx.assigned_bags.length > 0 && (
            <div className="pt-2 border-t border-[#F0EDE4]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-1">
                Assigned QR bags · {ctx.assigned_bags.length}
              </div>
              <div className="text-[10px] font-mono text-[#5C5A55] max-h-12 overflow-y-auto">
                {ctx.assigned_bags.slice(0, 12).map((b) => b.qr_code).join(", ")}
                {ctx.assigned_bags.length > 12 && ` +${ctx.assigned_bags.length - 12} more`}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Iter 195 — old inline banner removed; the always-visible
           "Bag delivery" section above now covers both states. */}

      {/* Start button */}
      {status === "pending" && (
        <button onClick={startTask} disabled={busy} data-testid="task-start"
                className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
          <Play size={16} weight="fill" />{busy ? "Starting…" : "Start job (GPS + time)"}
        </button>
      )}

      {/* Iter 189 — Live "on job" timer. Starts ticking when the task
           transitions to in_progress; freezes on completion. */}
      {canWork && t.start_time && (
        <JobTimer startIso={t.start_time}/>
      )}

      {/* Live collection summary */}
      {canWork && (bags.length > 0 || total.totalKg > 0) && (
        <div className="bg-[#0F1F0C] text-white rounded-lg p-4 space-y-2" data-testid="task-summary">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">Live collection</div>
              <div className="font-heading text-lg">
                {total.bagsN} bag{total.bagsN !== 1 ? "s" : ""} · {total.totalKg.toFixed(2)} kg
              </div>
            </div>
            <Scales size={26} weight="duotone" className="opacity-70"/>
          </div>
          {/* Per-waste-type row (info only, no allowance line — allowance is
              at the CATEGORY level and shown below). */}
          {Object.entries(total.perType).map(([wt, kg]) => {
            const it = (ctx.included_types || []).find((x) => x.waste_type_id === wt);
            return (
              <div key={wt} className="text-[11px] flex items-center justify-between border-t border-white/10 pt-1.5" data-testid={`task-total-${wt}`}>
                <span className="opacity-80">{it?.waste_name || wt}</span>
                <span className="font-mono opacity-80">{kg.toFixed(2)} kg</span>
              </div>
            );
          })}
          {/* Per-category rollup vs allowance */}
          {(ctx.category_allowances || []).map((ca) => {
            const kg = total.perCategory[ca.category] || 0;
            const over = ca.allowed_quantity_kg > 0 ? Math.max(0, kg - ca.allowed_quantity_kg) : 0;
            return (
              <div key={ca.category} className="text-[11px] flex items-center justify-between border-t border-white/20 pt-1.5 mt-1" data-testid={`task-cat-total-${ca.category}`}>
                <span className="font-medium">{ca.category} · category total</span>
                <span className="font-mono">
                  {kg.toFixed(2)} kg
                  {ca.allowed_quantity_kg > 0 && (
                    <span className={`ml-1 text-[10px] ${over > 0 ? "text-[#F4C7B9]" : "opacity-60"}`}>
                      / {ca.allowed_quantity_kg} incl{over > 0 ? ` · +${over.toFixed(2)} excess @₹${ca.excess_rate_per_kg}` : ""}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
          {/* Selected extra charges — line up their names + rates in the
              same summary card so the field staff sees exactly what will
              be billed. */}
          {extras.length > 0 && (ctx.extra_charges || []).length > 0 && (
            <div className="border-t border-white/20 pt-1.5 mt-1 space-y-1" data-testid="task-summary-extras">
              <div className="text-[9px] uppercase tracking-[0.18em] opacity-70">Extra charges</div>
              {(() => {
                // Iter 187 — group repeats so extras added multiple times
                // (via the +/- stepper) render as a single row with ×N.
                const counts = extras.reduce((m, id) => { m[id] = (m[id] || 0) + 1; return m; }, {});
                return Object.entries(counts).map(([eid, qty]) => {
                  const x = (ctx.extra_charges || []).find((e) => e.id === eid);
                  if (!x) return null;
                  const total = Number(x.amount) * qty;
                  return (
                    <div key={eid} className="text-[11px] flex items-center justify-between"
                          data-testid={`task-summary-extra-${eid}`}>
                      <span className="opacity-80">{extraChargeLabel(x)} × {qty}</span>
                      <span className="font-mono">+ ₹{total.toFixed(2)}</span>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}

      {/* QR MODE — bag list */}
      {canWork && ctx.use_qr_bags && (
        <>
          {/* Iter 170 — customer's in-house bags with multi-select quick-collect */}
          {(assignedBags.in_house || []).length > 0 && (
            <div className="bg-white border border-[#E5E2D9] rounded-lg p-3 space-y-2"
                 data-testid="task-assigned-bags">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-semibold">
                    Bags at this customer
                  </div>
                  <div className="text-[11px] text-[#8A8780]">
                    {assignedBags.totals?.total_at_customer || 0} in-house
                    · {assignedBags.totals?.collected_today || 0} collected today
                    · {assignedBags.totals?.collected_ever || 0} lifetime
                  </div>
                </div>
                <button onClick={selectAllAssigned}
                        data-testid="task-assigned-select-all"
                        className="h-8 px-3 rounded border border-[#E5E2D9] text-[11px]">
                  {selectedAssigned.size === (assignedBags.in_house || []).length ? "Clear" : "Select all"}
                </button>
              </div>
              <div className="divide-y divide-[#F0ECE3] border border-[#F0ECE3] rounded max-h-56 overflow-y-auto">
                {(assignedBags.in_house || []).map(b => (
                  <div key={b.qr_code}
                        data-testid={`task-assigned-row-${b.qr_code}`}
                        className={`flex items-center gap-2 px-2 py-1.5 text-[12px] ${
                          selectedAssigned.has(b.qr_code) ? "bg-[#F0F7EE]" : ""
                        }`}>
                    <input type="checkbox"
                            checked={selectedAssigned.has(b.qr_code)}
                            onChange={() => toggleAssigned(b.qr_code)}
                            data-testid={`task-assigned-check-${b.qr_code}`}
                            className="accent-[#2D5A27]"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[11px] truncate">{b.qr_code}</div>
                      <div className="text-[10px] text-[#8A8780]">
                        {b.color || "—"} · {b.waste_name || b.waste_category || "—"}
                        {b.weight_kg ? ` · ${Number(b.weight_kg).toFixed(2)} kg` : ""}
                        <span className={`ml-1 px-1 rounded-full text-[9px] ${
                          b.status === "delivered" ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#F9E4B7] text-[#7A5A11]"
                        }`}>{b.status}</span>
                      </div>
                    </div>
                    {/* Iter 187 — per-bag click-to-enter: reuse the scan-mode
                         weight-entry modal with this QR pre-filled. */}
                    <button type="button"
                             onClick={() => {
                               // Iter 187.1 + 189.1 — open the weight-entry
                               // modal without spinning up the QR scanner
                               // (we already know the code). manualMode
                               // suppresses the html5-qrcode video start.
                               setScanOpen(true);
                               setManualMode(true);
                               validateAndFetch(b.qr_code);
                             }}
                             disabled={busy || bags.some(x => x.qr_code === b.qr_code)}
                             data-testid={`task-assigned-enter-${b.qr_code}`}
                             title="Enter this bag's weight individually"
                             className="h-8 px-2 rounded-md border border-[#B5D5AC] text-[10px] font-semibold text-[#2D5A27] hover:bg-[#F0F7EE] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1">
                      {bags.some(x => x.qr_code === b.qr_code) ? "✓ Added" : "Enter kg →"}
                    </button>
                  </div>
                ))}
              </div>
              {/* Iter 189 — total weight across selected bags; disabled
                   until at least one bag is picked. */}
              <div className="flex items-center gap-2 pt-1">
                <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold flex-shrink-0">
                  Total weight (kg)
                </label>
                <input inputMode="decimal" value={bulkKgPerBag}
                       onChange={e => setBulkKgPerBag(e.target.value)}
                       placeholder="0.0"
                       disabled={selectedAssigned.size === 0}
                       data-testid="task-assigned-kg"
                       className="w-24 h-9 px-2 border border-[#E5E2D9] rounded text-sm font-mono text-right bg-white disabled:bg-[#F7F6F2] disabled:text-[#B5B2AB] disabled:cursor-not-allowed"/>
                <span className="text-[11px] text-[#8A8780]">kg</span>
                {selectedAssigned.size > 0 && Number(bulkKgPerBag) > 0 && (
                  <span className="ml-auto text-[11px] text-[#2D5A27] font-medium">
                    ≈ {(Number(bulkKgPerBag) / selectedAssigned.size).toFixed(2)} kg/bag
                  </span>
                )}
              </div>
              <button onClick={collectSelectedAssigned}
                      disabled={busy || selectedAssigned.size === 0 || !(Number(bulkKgPerBag) > 0)}
                      data-testid="task-assigned-collect"
                      className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2">
                <CheckCircle size={14} weight="fill"/>
                {busy ? "Collecting…" : `Mark ${selectedAssigned.size} as collected`}
              </button>
            </div>
          )}

          <div className="space-y-2" data-testid="task-bags-list">
            {bags.map((b, i) => (
              <div key={b.qr_code} className="bg-white border border-[#E5E2D9] rounded-lg p-3 flex items-center gap-3" data-testid={`task-bag-${b.qr_code}`}>
                <div className="w-8 h-8 rounded-md bg-[#D4E0D1] text-[#2D5A27] flex items-center justify-center text-xs font-heading">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{b.qr_code}</div>
                  <div className="text-[11px] text-[#5C5A55]">
                    {b.color || "—"} · {b.waste_name || b.waste_category || "—"} · <span className="font-mono">{Number(b.weight_kg).toFixed(2)} kg</span>
                    {b.manual && <span className="ml-1 text-[#C25D38]">· manual</span>}
                    {b.already_saved && <span className="ml-1 text-[#8A8780]">· saved</span>}
                  </div>
                </div>
                {!b.already_saved && (
                  <button onClick={() => removeBag(b.qr_code)} data-testid={`task-bag-remove-${b.qr_code}`}
                          className="p-2 text-[#C25D38] hover:bg-[#F9EDE7] rounded-md">
                    <Trash size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add bag chooser */}
          <div className="grid grid-cols-2 gap-2" data-testid="task-add-bag-choices">
            <button onClick={openScanner} disabled={busy} data-testid="task-scan-bag"
                    className="h-14 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex flex-col items-center justify-center gap-0.5">
              <span className="inline-flex items-center gap-1.5"><QrCode size={16} weight="bold" />Scan QR bag</span>
              <span className="text-[10px] opacity-70">Recommended</span>
            </button>
            <button onClick={openManualEntry} disabled={busy} data-testid="task-manual-bag"
                    className="h-14 border border-[#E5E2D9] hover:bg-[#F7F6F2] rounded-md text-sm font-medium inline-flex flex-col items-center justify-center gap-0.5">
              <span className="inline-flex items-center gap-1.5"><Keyboard size={16} />Enter manually</span>
              <span className="text-[10px] text-[#8A8780]">Fallback</span>
            </button>
          </div>

          {/* Superadmin-allowed non-QR panel — shown ADDITIONALLY to QR mode
              when the tenant has whitelisted certain categories (D-BMW /
              Biomedical). Field staff enter bulky / non-baggable weights
              here in the SAME session as the scanned bags. */}
          {ctx.non_qr_allowed && (ctx.non_qr_categories || []).length > 0 && (
            <div className="bg-[#F9E4B7]/25 border border-[#F9E4B7] rounded-lg p-4 space-y-3" data-testid="task-non-qr-inline">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A5A11]">
                  Non-QR collection · office-allowed
                </div>
                <div className="text-[11px] text-[#5C5A55]">
                  Add bulky / non-baggable weights for:{" "}
                  <strong className="text-[#7A5A11]">{ctx.non_qr_categories.join(", ")}</strong>
                </div>
              </div>
              {(ctx.included_types || [])
                .filter((it) => ctx.non_qr_categories.includes(it.category))
                .map((it) => (
                  <div key={it.waste_type_id} className="flex items-center gap-3" data-testid={`task-inline-non-qr-${it.waste_type_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.waste_name}</div>
                      <div className="text-[10px] text-[#8A8780]">{it.category}</div>
                    </div>
                    <div className="w-24">
                      <input inputMode="decimal" value={nonQrWeights[it.waste_type_id] || ""}
                              onChange={(e) => setNonQrWeights((w) => ({ ...w, [it.waste_type_id]: e.target.value }))}
                              placeholder="0.0"
                              data-testid={`task-inline-non-qr-weight-${it.waste_type_id}`}
                              className="w-full h-10 px-2 border border-[#E5E2D9] rounded text-sm font-mono text-right bg-white"/>
                    </div>
                    <div className="text-xs text-[#8A8780] w-6">kg</div>
                  </div>
                ))}
            </div>
          )}
        </>
      )}

      {/* NON-QR MODE — direct manual weight entry */}
      {canWork && !ctx.use_qr_bags && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 space-y-3" data-testid="task-non-qr-entry">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Non-QR collection</div>
            <div className="text-sm text-[#5C5A55]">
              Enter the weight collected for each included waste category.
            </div>
          </div>
          {(ctx.included_types || []).length === 0 && (
            <div className="text-xs text-[#C25D38] bg-[#F9EDE7] rounded p-2">
              <Warning size={12} className="inline -mt-0.5 mr-1"/>
              Package has no included waste categories configured.
            </div>
          )}
          {(ctx.included_types || []).map((it) => (
            <div key={it.waste_type_id} className="flex items-center gap-3" data-testid={`task-non-qr-line-${it.waste_type_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{it.waste_name}</div>
                <div className="text-[10px] text-[#8A8780]">{it.category}</div>
              </div>
              <div className="w-28">
                <input inputMode="decimal" value={nonQrWeights[it.waste_type_id] || ""}
                        onChange={(e) => setNonQrWeights((w) => ({ ...w, [it.waste_type_id]: e.target.value }))}
                        placeholder="0.0 kg"
                        data-testid={`task-non-qr-weight-${it.waste_type_id}`}
                        className="w-full h-10 px-2 border border-[#E5E2D9] rounded text-sm font-mono text-right"/>
              </div>
            </div>
          ))}
          {/* Category allowance summary (once, not per waste type) */}
          {(ctx.category_allowances || []).length > 0 && (
            <div className="pt-2 border-t border-[#F0EDE4] text-[10px] text-[#5C5A55] space-y-0.5">
              {ctx.category_allowances.map((ca) => (
                <div key={ca.category}>
                  <strong>{ca.category}</strong> total allowance: {ca.allowed_quantity_kg} kg · excess ₹{ca.excess_rate_per_kg}/kg
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notes + confirm */}
      {canWork && (
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">
              Notes (optional)
            </label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                       placeholder="Any remark for the office…"
                       data-testid="task-notes"
                       className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
          {(ctx.extra_charges || []).length > 0 && (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-3" data-testid="task-extras">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-2 font-semibold">
                Extra charges
              </div>
              <div className="space-y-1.5">
                {ctx.extra_charges.map((x) => {
                  const qty = extras.filter((id) => id === x.id).length;
                  const inc = () => setExtras([...extras, x.id]);
                  const dec = () => {
                    const i = extras.indexOf(x.id);
                    if (i < 0) return;
                    const next = [...extras]; next.splice(i, 1); setExtras(next);
                  };
                  return (
                    <div key={x.id} className="flex items-center gap-2 text-xs"
                         data-testid={`task-extra-row-${x.id}`}>
                      <button type="button" onClick={inc}
                               data-testid={`task-extra-plus-${x.id}`}
                               aria-label={`Add one more ${extraChargeLabel(x)}`}
                               className="w-8 h-8 rounded-full bg-[#2D5A27] hover:bg-[#3A7033] text-white inline-flex items-center justify-center">
                        <Plus size={12} weight="bold"/>
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{extraChargeLabel(x)}</div>
                        <div className="text-[10px] text-[#8A8780]">
                          ₹{Number(x.amount).toFixed(2)} each
                        </div>
                      </div>
                      {qty > 0 && (
                        <button type="button" onClick={dec}
                                 data-testid={`task-extra-minus-${x.id}`}
                                 aria-label="Remove one"
                                 className="w-7 h-7 rounded-full border border-[#F4C7B9] text-[#7A2E18] hover:bg-[#F9EDE7] inline-flex items-center justify-center">
                          <Minus size={11} weight="bold"/>
                        </button>
                      )}
                      {qty > 0 && (
                        <>
                          <span data-testid={`task-extra-qty-${x.id}`}
                                 className="min-w-[2.25rem] text-center text-[13px] font-mono font-semibold text-[#2D5A27]">
                            ×{qty}
                          </span>
                          <span className="text-[11px] font-mono text-[#5C5A55] w-16 text-right">
                            ₹{(Number(x.amount) * qty).toFixed(0)}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <button onClick={submit} disabled={busy}
                  data-testid="task-confirm"
                  className="w-full h-12 bg-[#C89E3E] hover:bg-[#B38A2F] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <Receipt size={16} weight="fill" />
            {busy ? "Generating…" : (ctx.invoice_verification_required
              ? "Confirm — send draft to office"
              : "Confirm — generate invoice")}
            <ArrowRight size={12} weight="bold" />
          </button>
          {ctx.invoice_verification_required && (
            <div className="text-[10px] text-[#8A8780] text-center">
              Office will verify & finalise the invoice.
            </div>
          )}
        </div>
      )}

      {finished && (
        <div className="bg-[#D4E0D1] text-[#1A3317] rounded-lg p-4 text-sm text-center">
          <CheckCircle size={24} weight="fill" className="inline-block mb-1"/>
          <div>This task is <strong>{status}</strong>.</div>
        </div>
      )}

      {/* Scan modal */}
      {scanOpen && (
        <ScanModal
          onCancel={() => { setScanOpen(false); setEntryFor(null); setEntryWeights({}); }}
          onCode={(code) => validateAndFetch(code)}
          manualMode={manualMode}
          manualCode={manualCode}
          setManualCode={setManualCode}
          busy={busy}
          entryFor={entryFor}
          entryWeights={entryWeights}
          setEntryWeights={setEntryWeights}
          onSave={saveBagEntry}
          included={
            // Iter 189 — restrict the weight-entry form to the customer's
            // primary_waste_types only when they have any set.
            (() => {
              const primary = c.primary_waste_types || [];
              const all = ctx.included_types || [];
              const filtered = primary.length
                ? all.filter(it => primary.includes(it.waste_type_id))
                : all;
              return filtered.length ? filtered : all;
            })()
          }
          onSwitchManual={() => setManualMode(true)}
        />
      )}

      {/* Iter 187 — Pre-submit confirmation with totals */}
      {confirmOpen && (
        <ConfirmSubmitModal
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doSubmit}
          busy={busy}
          asDraft={ctx.invoice_verification_required}
          total={total}
          extraCounts={previewTotals.extraCounts}
          extraCharges={ctx.extra_charges || []}
          pkgName={pkg?.name}
          pkgPrice={previewTotals.pkgPrice}
          excessSum={previewTotals.excessSum}
          extraSum={previewTotals.extraSum}
          payable={previewTotals.payable}
          prepaid={ctx.service_request?.prepaid}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Scan modal
// ═════════════════════════════════════════════════════════════════════
function ScanModal({
  onCancel, onCode, manualMode, manualCode, setManualCode, busy,
  entryFor, entryWeights, setEntryWeights, onSave, included, onSwitchManual,
}) {
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const [cameraErr, setCameraErr] = useState("");
  const inWeighing = !!entryFor;

  // Safe teardown — html5-qrcode's stop() throws SYNCHRONOUSLY when the
  // scanner never entered SCANNING state (e.g., camera init failed). A
  // .catch() only catches promise rejections, not sync throws, so we wrap.
  const safeStop = (s) => {
    if (!s) return;
    try {
      const p = s.stop();
      if (p && typeof p.catch === "function") {
        p.catch(() => {}).finally(() => { try { s.clear?.(); } catch { /* noop */ } });
      } else {
        try { s.clear?.(); } catch { /* noop */ }
      }
    } catch { /* scanner never started */
      try { s.clear?.(); } catch { /* noop */ }
    }
  };

  useEffect(() => {
    if (manualMode || inWeighing) return;
    let active = true;
    const id = "task-html5-qr";
    startedRef.current = false;
    const start = async () => {
      try {
        const el = document.getElementById(id);
        if (!el) return;
        const scanner = new Html5Qrcode(id, { verbose: false });
        const onDecode = (decoded) => {
          if (!active) return;
          const cleaned = String(decoded || "").trim().toUpperCase();
          if (cleaned) {
            active = false;
            safeStop(scanner);
            onCode(cleaned);
          }
        };
        const cfg = { fps: 10, qrbox: { width: 220, height: 220 } };
        try {
          await scanner.start({ facingMode: "environment" }, cfg, onDecode, () => {});
        } catch {
          const cams = await Html5Qrcode.getCameras();
          if (!cams || cams.length === 0) throw new Error("NotFoundError: No cameras");
          await scanner.start(cams[cams.length - 1].id, cfg, onDecode, () => {});
        }
        // Only wire the ref once we know start() succeeded so cleanup
        // doesn't fire stop() on an idle scanner.
        scannerRef.current = scanner;
        startedRef.current = true;
      } catch (e) {
        const msg = String(e?.message || e);
        if (msg.includes("NotAllowedError") || msg.includes("Permission")) {
          setCameraErr("Camera permission denied. Tap 'Type instead' to enter the QR code manually.");
        } else if (msg.includes("NotFoundError") || msg.includes("no camera") || msg.includes("Requested device not found")) {
          setCameraErr("No camera found on this device. Tap 'Type instead' to enter the QR code manually.");
        } else {
          setCameraErr(msg);
        }
      }
    };
    start();
    return () => {
      active = false;
      if (startedRef.current) safeStop(scannerRef.current);
      scannerRef.current = null;
    };
  }, [manualMode, inWeighing, onCode]);

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center" onClick={onCancel} data-testid="task-scan-modal">
      <div className="bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading text-lg inline-flex items-center gap-1.5">
            {manualMode ? <Keyboard size={16}/> : <QrCode size={16}/>}
            {inWeighing ? "Enter weight" : manualMode ? "Enter QR bag ID" : "Scan QR bag"}
          </div>
          <button onClick={onCancel} data-testid="task-scan-close"
                  className="p-1.5 rounded hover:bg-[#F7F6F2]">
            <X size={16} />
          </button>
        </div>

        {/* Weighing step */}
        {inWeighing ? (
          <div className="space-y-3" data-testid="task-weigh-panel">
            <div className="bg-[#F7F6F2] rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Bag details</div>
              <div className="font-mono text-sm mt-0.5">{entryFor.qr_code}</div>
              <div className="text-[11px] text-[#5C5A55]">
                {entryFor.color ? <><strong>{entryFor.color}</strong> · </> : null}
                {entryFor.bag_type || "—"} · {entryFor.waste_name || entryFor.waste_category || "—"}
              </div>
              {entryFor.customer && (
                <div className="text-[11px] text-[#2D5A27] mt-1">
                  <User size={10} className="inline -mt-0.5 mr-0.5"/>
                  {entryFor.customer.name}
                  {entryFor.package && <> · <Package size={10} className="inline -mt-0.5 mx-0.5"/>{entryFor.package.name}</>}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#2D5A27] mt-1">
                Status · {entryFor.status || "—"}
              </div>
            </div>
            {/* Weight inputs — restricted to included waste types (if the
                bag already has a specific waste_type_id, only that row
                shows; otherwise any included row can be filled). */}
            {(() => {
              const bagWt = entryFor.waste_type_id;
              const rows = bagWt
                ? included.filter((it) => it.waste_type_id === bagWt)
                : included;
              if (rows.length === 0) {
                return (
                  <div className="text-xs text-[#C25D38] bg-[#F9EDE7] rounded p-2">
                    <Warning size={12} className="inline -mt-0.5 mr-1"/>
                    This bag&apos;s waste type is not included in the customer&apos;s package.
                  </div>
                );
              }
              return rows.map((it) => (
                <div key={it.waste_type_id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.waste_name}</div>
                    <div className="text-[10px] text-[#8A8780]">{it.category}</div>
                  </div>
                  <input inputMode="decimal" autoFocus
                          value={entryWeights[it.waste_type_id] ?? ""}
                          onChange={(e) => setEntryWeights({ ...entryWeights, [it.waste_type_id]: e.target.value })}
                          placeholder="0.0"
                          data-testid={`task-weigh-${it.waste_type_id}`}
                          className="w-24 h-11 px-2 border border-[#E5E2D9] rounded text-sm font-mono text-right"/>
                  <div className="text-xs text-[#8A8780] w-6">kg</div>
                </div>
              ));
            })()}
            <div className="flex gap-2 pt-1">
              <button onClick={onCancel} data-testid="task-weigh-cancel" className="flex-1 h-11 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
              <button onClick={onSave} data-testid="task-weigh-save"
                      className="flex-1 h-11 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <CheckCircle size={14} weight="bold" />Save bag
              </button>
            </div>
          </div>
        ) : manualMode ? (
          <div className="space-y-3" data-testid="task-manual-panel">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1.5">QR bag ID</label>
              <input value={manualCode} onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                      placeholder="RB000020" autoFocus
                      data-testid="task-manual-code"
                      className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono uppercase"/>
            </div>
            <div className="flex gap-2">
              <button onClick={onCancel} className="flex-1 h-11 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
              <button onClick={() => onCode(manualCode)} disabled={busy || !manualCode.trim()}
                      data-testid="task-manual-lookup"
                      className="flex-1 h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <ArrowRight size={14} weight="bold" />{busy ? "…" : "Look up"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="task-camera-panel">
            <div id="task-html5-qr" className="w-full aspect-square bg-black rounded-md overflow-hidden"/>
            <div className="text-[11px] text-[#5C5A55] text-center">
              <Camera size={12} className="inline -mt-0.5 mr-1"/>
              Point the camera at the bag&apos;s QR sticker
            </div>
            {cameraErr && (
              <div className="text-xs text-[#C25D38] bg-[#F9EDE7] rounded p-2">
                Camera error: {cameraErr}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={onCancel} className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
              <button onClick={onSwitchManual} data-testid="task-switch-manual"
                       className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-sm inline-flex items-center justify-center gap-1.5">
                <Keyboard size={14}/>Type instead
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Iter 187 — Pre-submit confirmation modal. Shows total bags + weight +
// payable so the field staff double-checks before draft/issue.
// ═════════════════════════════════════════════════════════════════════
function ConfirmSubmitModal({
  onCancel, onConfirm, busy, asDraft, total, extraCounts, extraCharges,
  pkgName, pkgPrice, excessSum, extraSum, payable, prepaid,
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
         onClick={onCancel} data-testid="task-confirm-modal">
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md border border-[#E5E2D9] shadow-lg"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#F0ECE3]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#2D5A27] font-semibold">
              Confirm collection
            </div>
            <div className="font-heading text-[16px] text-[#1A1A1A]">
              Review before {asDraft ? "sending draft" : "issuing invoice"}
            </div>
          </div>
          <button onClick={onCancel} data-testid="task-confirm-close"
                   className="p-1.5 rounded hover:bg-[#F7F6F2]" aria-label="Close">
            <X size={14}/>
          </button>
        </div>
        <div className="p-4 space-y-3 text-[13px]">
          <div className="bg-[#0F1F0C] text-white rounded-md p-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.18em] opacity-70">Bags collected</span>
              <span className="text-[10px] uppercase tracking-[0.18em] opacity-70">Total weight</span>
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className="font-heading text-2xl" data-testid="task-confirm-bags">
                {total.bagsN}
              </span>
              <span className="font-heading text-2xl font-mono" data-testid="task-confirm-kg">
                {total.totalKg.toFixed(2)} kg
              </span>
            </div>
          </div>

          <div className="border border-[#F0ECE3] rounded-md divide-y divide-[#F0ECE3]">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-[#5C5A55]">Package · <strong>{pkgName || "—"}</strong></span>
              <span className="font-mono" data-testid="task-confirm-pkg">₹{pkgPrice.toFixed(2)}</span>
            </div>
            {excessSum > 0 && (
              <div className="flex items-center justify-between px-3 py-2 bg-[#FFF7E6]">
                <span className="text-[#7A5A11]">Excess weight charges</span>
                <span className="font-mono text-[#7A5A11]" data-testid="task-confirm-excess">
                  + ₹{excessSum.toFixed(2)}
                </span>
              </div>
            )}
            {Object.entries(extraCounts).map(([eid, qty]) => {
              const x = (extraCharges || []).find((e) => e.id === eid);
              if (!x) return null;
              const amt = Number(x.amount) * qty;
              return (
                <div key={eid} className="flex items-center justify-between px-3 py-1.5"
                      data-testid={`task-confirm-extra-${eid}`}>
                  <span className="text-[#5C5A55] truncate">
                    {(x.type || "Extra").replace(/_/g, " ")} × {qty}
                  </span>
                  <span className="font-mono text-[#5C5A55]">+ ₹{amt.toFixed(2)}</span>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between px-3 py-3 bg-[#2D5A27] text-white rounded-md">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] opacity-80">Subtotal</div>
              <div className="text-[9px] opacity-70">before GST · final invoice adds tax</div>
            </div>
            <span className="font-heading text-2xl font-mono" data-testid="task-confirm-payable">
              ₹{payable.toFixed(2)}
            </span>
          </div>
          <PrepaidSettlement prepaid={prepaid} total={payable} testid="task-prepaid"/>
          {prepaid && <div className="text-[10px] text-[#8A8780]">Figures before GST — the final invoice deducts the prepaid amount from the GST-inclusive total.</div>}

          {asDraft && (
            <div className="text-[11px] text-[#7A5A11] bg-[#FFF7E6] border border-[#F5C518] rounded p-2 inline-flex items-start gap-1.5">
              <Info size={12} className="mt-0.5 flex-shrink-0"/>
              <span>
                This will be saved as a <strong>draft</strong>.
                Office will verify and finalise the invoice.
              </span>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-[#F0ECE3] flex gap-2">
          <button onClick={onCancel} data-testid="task-confirm-cancel"
                   disabled={busy}
                   className="flex-1 h-11 rounded-md border border-[#E5E2D9] text-[13px] font-semibold text-[#5C5A55] hover:bg-[#F7F6F2] disabled:opacity-50">
            Review again
          </button>
          <button onClick={onConfirm} disabled={busy}
                   data-testid="task-confirm-submit"
                   className="flex-1 h-11 rounded-md bg-[#C89E3E] hover:bg-[#B38A2F] text-white text-[13px] font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1.5">
            <Receipt size={14} weight="fill"/>
            {busy ? "Submitting…" : (asDraft ? "Send draft" : "Issue invoice")}
          </button>
        </div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Iter 189 — Live job timer. Ticks every second from task.start_time.
// ═════════════════════════════════════════════════════════════════════
function JobTimer({ startIso }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const startMs = new Date(startIso).getTime();
  const elapsedSec = Math.max(0, Math.floor((now - startMs) / 1000));
  const h = Math.floor(elapsedSec / 3600);
  const m = Math.floor((elapsedSec % 3600) / 60);
  const s = elapsedSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="bg-[#2D5A27] text-white rounded-lg px-4 py-3 flex items-center gap-3"
          data-testid="task-timer">
      <Clock size={22} weight="fill" className="opacity-80 flex-shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">On job — timer running</div>
        <div className="font-mono text-2xl leading-none mt-0.5" data-testid="task-timer-value">
          {h > 0 ? `${pad(h)}:` : ""}{pad(m)}:{pad(s)}
        </div>
      </div>
      <span className="relative flex h-2 w-2 flex-shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70 animate-ping"/>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"/>
      </span>
    </div>
  );
}

